import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Typography, Button, Icon, Select, Tag, ButtonMenu } from '@ann/web-components';
import {
  HeaderContainer,
  HeaderLeftContainer,
  TypographyContainer,
  HeaderRightContainer,
  IconContainer,
  AgentSearchContainer,
} from './Header.styled';
import { useAgent } from '../../context';
import { getAgentList } from '../../../../../services/agents';

const Header = () => {
  const navigate = useNavigate();
  const { agentId } = useParams();
  const { agent } = useAgent();

  const [searchTerm, setSearchTerm] = useState('');
  const [agents, setAgents] = useState([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);

  // Fetch lista de agentes
  const fetchAgentsList = useCallback(async () => {
    setIsLoadingAgents(true);
    try {
      const response = await getAgentList();
      if (response?.data?.agents) {
        const agentsList = Array.isArray(response.data.agents) ? response.data.agents : [];
        setAgents(agentsList.map((a) => ({
          label: a.name,
          value: a.uuid,
          shortLabel: a.name?.substring(0, 2).toUpperCase() || '',
          description: a.description || '',
        })));
      }
    } catch (err) {
      console.error('Error fetching agents list:', err);
    } finally {
      setIsLoadingAgents(false);
    }
  }, []);

  useEffect(() => {
    fetchAgentsList();
  }, [fetchAgentsList]);

  // Resetear búsqueda cuando cambia el agente
  useEffect(() => {
    setSearchTerm('');
  }, [agentId]);

  const filteredAgents = useMemo(() => {
    if (!searchTerm.trim()) return agents;

    const searchLower = searchTerm.toLowerCase();
    return agents.filter((a) =>
      a.label.toLowerCase().includes(searchLower) ||
      a.description.toLowerCase().includes(searchLower)
    );
  }, [searchTerm, agents]);

  const handleSearch = useCallback((searchValue) => {
    setSearchTerm(searchValue);
  }, []);

  const handleSelection = useCallback((value) => {
    if (value) {
      navigate(`/v2/playground2/${value}/configuration`);
      setSearchTerm('');
    }
  }, [navigate]);

  const handleBackClick = useCallback(() => {
    navigate('/v2/AgentsView');
  }, [navigate]);

  const handleActionSelect = useCallback((action) => {
    if (action === 'archive') {
      // TODO: Implementar modal de archivo
      console.log('Archive action');
    } else if (action === 'share') {
      // TODO: Implementar compartir
      console.log('Share action');
    }
  }, []);

  return (
    <HeaderContainer>
      <HeaderLeftContainer>
        <IconContainer onClick={handleBackClick}>
          <Icon name="arrowLeft" size="medium" />
        </IconContainer>
        <TypographyContainer title={agent.name}>
          <Typography category="title" size="large" weight="bold" maxCharacters={30}>
            {agent.name || 'Agent'}
          </Typography>
        </TypographyContainer>
        <Tag
          color="neutral"
          onClose={() => {}}
          size="medium"
          text={agent.status === 'active' ? 'Published' : 'Draft'}
        />

        <AgentSearchContainer>
          <Select
            key={`agent-select-${agentId}`}
            placeholder={isLoadingAgents ? 'Loading agents...' : 'Search and select agent'}
            onChange={handleSelection}
            size="small"
            width="auto"
            minWidth="auto"
            disabled={isLoadingAgents}
          >
            <Select.Menu width="278px" minWidth="278px">
              <Select.Search
                placeholder="Search agents..."
                onChange={handleSearch}
                label=""
              />
              {isLoadingAgents ? (
                <Select.Item value="" label="Loading agents..." disabled>
                  Loading agents...
                </Select.Item>
              ) : filteredAgents.length === 0 ? (
                <Select.Item value="" label="No agents found" disabled>
                  No agents found
                </Select.Item>
              ) : (
                filteredAgents.map((a) => (
                  <Select.Item
                    key={a.value}
                    value={a.value}
                    label={a.label}
                    shortLabel={a.shortLabel}
                  >
                    <div>
                      <div>{a.label}</div>
                      {a.description && (
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                          {a.description}
                        </div>
                      )}
                    </div>
                  </Select.Item>
                ))
              )}
            </Select.Menu>
          </Select>
        </AgentSearchContainer>
      </HeaderLeftContainer>

      <HeaderRightContainer>
        <ButtonMenu
          iconPosition="right"
          label="Actions"
          menuPosition="bottom"
          onChange={handleActionSelect}
          size="medium"
          type="secondary"
          width="auto"
        >
          <ButtonMenu.Menu>
            <ButtonMenu.Item label="Archive" value="archive">
              Archive
            </ButtonMenu.Item>
            <ButtonMenu.Item label="Share" value="share">
              Share
            </ButtonMenu.Item>
          </ButtonMenu.Menu>
        </ButtonMenu>
      </HeaderRightContainer>
    </HeaderContainer>
  );
};

export default Header;

